
import { useEffect, useState } from "react";

import Header from "../components/Header";
import Footer from "../components/Footer";

import API_BASE_URL from "../config/config";

import "./Jobs.css";


function Jobs() {

    // =========================================================
    // STATES
    // =========================================================

    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [expandedJobs, setExpandedJobs] = useState({});
    const [currentPage, setCurrentPage] = useState(1);

    const jobsPerPage = 5;


    // =========================================================
    // API URL
    // =========================================================

    const API_URL = API_BASE_URL.replace(/\/$/, "");


    // =========================================================
    // FETCH JOBS
    // =========================================================

    useEffect(() => {

        const fetchJobs = async () => {

            try {

                setLoading(true);
                setError("");

                const response = await fetch(
                    `${API_URL}/user-jobs`,
                    {
                        method: "GET",
                        headers: {
                            Accept: "application/json"
                        }
                    }
                );

                const text = await response.text();

                console.log(
                    "Jobs API URL:",
                    `${API_URL}/user-jobs`
                );

                console.log("Jobs API Status:", response.status);
                console.log("Jobs API Response:", text);

                let data;

                try {

                    data = JSON.parse(text);

                } catch (jsonError) {

                    throw new Error(
                        `Server returned invalid JSON. HTTP Status: ${response.status}`
                    );

                }

                if (!response.ok) {

                    throw new Error(
                        data.message ||
                        `Failed to fetch jobs (${response.status})`
                    );

                }

                // API RESPONSE

                if (Array.isArray(data.jobs)) {

                    setJobs(data.jobs);

                } else if (Array.isArray(data.data)) {

                    setJobs(data.data);

                } else if (Array.isArray(data)) {

                    setJobs(data);

                } else {

                    setJobs([]);

                    setError(
                        data.message || "No jobs available."
                    );

                }

            } catch (error) {

                console.error("Jobs API Error:", error);

                setError(
                    error.message || "Unable to load jobs."
                );

                setJobs([]);

            } finally {

                setLoading(false);

            }

        };

        fetchJobs();

    }, [API_URL]);


    // =========================================================
    // GET IMAGE URL
    // =========================================================

    const getImageUrl = (image) => {

        if (!image) {
            return "";
        }

        if (
            image.startsWith("http://") ||
            image.startsWith("https://")
        ) {
            return image;
        }

        const baseUrl = API_URL.replace(/\/api$/, "");

        return `${baseUrl}/storage/jobs/${image.replace(/^\/+/, "")}`;

    };


    // =========================================================
    // GET PDF VIEW URL
    // =========================================================

    const getPdfViewUrl = (download) => {

        if (!download) {
            return "";
        }

        if (
            download.startsWith("http://") ||
            download.startsWith("https://")
        ) {
            return download;
        }

        const baseUrl = API_URL.replace(/\/api$/, "");

        return `${baseUrl}/storage/jobs/${download.replace(/^\/+/, "")}`;

    };


    // =========================================================
    // GET PDF DOWNLOAD URL
    // =========================================================

    const getPdfDownloadUrl = (jobId) => {

        return `${API_URL}/user-jobs/${jobId}/download`;

    };


    // =========================================================
    // READ MORE / READ LESS
    // =========================================================

    const toggleReadMore = (jobId) => {

        setExpandedJobs((previous) => ({
            ...previous,
            [jobId]: !previous[jobId]
        }));

    };


    // =========================================================
    // GET PLAIN TEXT
    // =========================================================

    const getPlainText = (html) => {

        if (!html) {
            return "";
        }

        const tempDiv = document.createElement("div");

        tempDiv.innerHTML = html;

        return (
            tempDiv.textContent ||
            tempDiv.innerText ||
            ""
        );

    };


    // =========================================================
    // DESCRIPTION LIMIT
    // =========================================================

    const descriptionLimit = 100;


    // =========================================================
    // PAGINATION
    // =========================================================

    const totalPages = Math.ceil(
        jobs.length / jobsPerPage
    );

    const startIndex =
        (currentPage - 1) * jobsPerPage;

    const endIndex =
        startIndex + jobsPerPage;

    const currentJobs = jobs.slice(
        startIndex,
        endIndex
    );


    // =========================================================
    // RESPONSIVE PAGE NUMBERS
    // =========================================================

    const getVisiblePages = () => {

        const pages = [];

        // Show all pages when the total is small.
        if (totalPages <= 7) {

            for (let page = 1; page <= totalPages; page++) {
                pages.push(page);
            }

            return pages;

        }

        // First page is always visible.
        pages.push(1);

        let startPage = Math.max(2, currentPage - 1);

        let endPage = Math.min(
            totalPages - 1,
            currentPage + 1
        );

        // Pages near the beginning.
        if (currentPage <= 3) {

            startPage = 2;
            endPage = 4;

        }

        // Pages near the end.
        if (currentPage >= totalPages - 2) {

            startPage = totalPages - 3;
            endPage = totalPages - 1;

        }

        // Left ellipsis.
        if (startPage > 2) {
            pages.push("...");
        }

        // Nearby page numbers.
        for (
            let page = startPage;
            page <= endPage;
            page++
        ) {
            pages.push(page);
        }

        // Right ellipsis.
        if (endPage < totalPages - 1) {
            pages.push("...");
        }

        // Last page is always visible.
        pages.push(totalPages);

        return pages;

    };


    // =========================================================
    // CHANGE PAGE
    // =========================================================

    const changePage = (page) => {

        if (
            page < 1 ||
            page > totalPages ||
            page === currentPage
        ) {
            return;
        }

        setCurrentPage(page);

        window.scrollTo({
            top: 500,
            behavior: "smooth"
        });

    };


    // =========================================================
    // FORMAT DATE
    // =========================================================

    const formatDate = (date) => {

        if (!date) {
            return "";
        }

        return date;

    };


    // =========================================================
    // COMMON PAGE HERO
    // =========================================================

    const renderHero = () => (

        <section className="jobs-hero">

            <div className="jobs-container">

                <span className="jobs-badge">
                    CAREER OPPORTUNITIES
                </span>

                <h1>
                    Jobs{" "}
                    &{" "}
                    <strong>Careers</strong>
                </h1>

                <p>
                    Explore career opportunities and join our growing team.
                </p>

            </div>

        </section>

    );


    // =========================================================
    // LOADING
    // =========================================================

    if (loading) {

        return (
            <>
                <Header />

                <main className="jobs-page">

                    {renderHero()}

                    <section className="jobs-list-section">

                        <div className="jobs-container">

                            <div className="jobs-loading">

                                <div className="jobs-spinner" />

                                <span>
                                    Loading job opportunities...
                                </span>

                            </div>

                        </div>

                    </section>

                </main>

                <Footer />
            </>
        );

    }


    // =========================================================
    // ERROR
    // =========================================================

    if (error) {

        return (
            <>
                <Header />

                <main className="jobs-page">

                    {renderHero()}

                    <section className="jobs-list-section">

                        <div className="jobs-container">

                            <div className="jobs-error">
                                {error}
                            </div>

                        </div>

                    </section>

                </main>

                <Footer />
            </>
        );

    }


    // =========================================================
    // MAIN
    // =========================================================

    return (
        <>
            <Header />

            <main className="jobs-page">

                {/* HERO */}

                {renderHero()}


                {/* JOBS SECTION */}

                <section className="jobs-list-section">

                    <div className="jobs-container">

                        {/* SECTION HEADING */}

                        <div className="jobs-section-heading">

                            <span>JOIN OUR TEAM</span>

                            <h2>
                                Current{" "}
                                <strong>Openings</strong>
                            </h2>

                            <p>
                                Explore our current job opportunities
                                and find the right position for you.
                            </p>

                        </div>


                        {/* NO JOBS */}

                        {jobs.length === 0 ? (

                            <div className="jobs-empty">

                                <div className="jobs-empty-icon">
                                    💼
                                </div>

                                <h3>No Job Openings</h3>

                                <p>
                                    There are currently no open positions.
                                    Please check again later.
                                </p>

                            </div>

                        ) : (

                            <>

                                {/* JOB GRID */}

                                <div className="jobs-grid">

                                    {currentJobs.map((job, index) => {

                                        const jobId =
                                            job.job_id ||
                                            job.id ||
                                            index;

                                        const plainDescription =
                                            getPlainText(job.description);

                                        const isLongDescription =
                                            plainDescription.length >
                                            descriptionLimit;

                                        const isExpanded =
                                            expandedJobs[jobId];

                                        return (

                                            <article
                                                className="job-card"
                                                key={jobId}
                                            >

                                                {/* JOB IMAGE */}

                                                {job.image ? (

                                                    <div className="job-image-wrapper">

                                                        <img
                                                            src={getImageUrl(job.image)}
                                                            alt={
                                                                job.title ||
                                                                "Job Opportunity"
                                                            }
                                                            className="job-image"
                                                            onError={(e) => {
                                                                e.currentTarget.style.display =
                                                                    "none";
                                                            }}
                                                        />

                                                    </div>

                                                ) : (

                                                    <div className="job-image-placeholder">

                                                        <span>💼</span>

                                                        <small>
                                                            Job Opportunity
                                                        </small>

                                                    </div>

                                                )}


                                                {/* TOP */}

                                                <div className="job-card-top">

                                                    <div className="job-icon">
                                                        💼
                                                    </div>

                                                    {job.date_added && (

                                                        <span className="job-date">
                                                            {formatDate(job.date_added)}
                                                        </span>

                                                    )}

                                                </div>


                                                {/* TITLE */}

                                                <h3 className="job-title">
                                                    {job.title ||
                                                        job.job_title ||
                                                        "Job Opportunity"}
                                                </h3>


                                                {/* AUTHOR */}

                                                {job.author && (

                                                    <div className="job-author">

                                                        <span>By</span>

                                                        <strong>
                                                            {job.author}
                                                        </strong>

                                                    </div>

                                                )}


                                                {/* DESCRIPTION */}

                                                {job.description && (

                                                    <div className="job-description">

                                                        {!isExpanded ? (

                                                            <>

                                                                <p>
                                                                    {isLongDescription
                                                                        ? `${plainDescription
                                                                            .substring(
                                                                                0,
                                                                                descriptionLimit
                                                                            )
                                                                            .trim()}...`
                                                                        : plainDescription}
                                                                </p>

                                                                {isLongDescription && (

                                                                    <button
                                                                        type="button"
                                                                        className="read-more-btn"
                                                                        onClick={() =>
                                                                            toggleReadMore(jobId)
                                                                        }
                                                                    >
                                                                        Read More
                                                                    </button>

                                                                )}

                                                            </>

                                                        ) : (

                                                            <>

                                                                <div
                                                                    className="job-full-description"
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: job.description
                                                                    }}
                                                                />

                                                                <button
                                                                    type="button"
                                                                    className="read-more-btn"
                                                                    onClick={() =>
                                                                        toggleReadMore(jobId)
                                                                    }
                                                                >
                                                                    Read Less
                                                                </button>

                                                            </>

                                                        )}

                                                    </div>

                                                )}


                                                {/* JOB DETAILS */}

                                                <div className="job-details">

                                                    {job.location && (

                                                        <div className="job-detail">

                                                            <span className="job-detail-icon">
                                                                📍
                                                            </span>

                                                            <span>
                                                                {job.location}
                                                            </span>

                                                        </div>

                                                    )}

                                                    {job.experience && (

                                                        <div className="job-detail">

                                                            <span className="job-detail-icon">
                                                                💼
                                                            </span>

                                                            <span>
                                                                {job.experience}
                                                            </span>

                                                        </div>

                                                    )}

                                                    {job.qualification && (

                                                        <div className="job-detail">

                                                            <span className="job-detail-icon">
                                                                🎓
                                                            </span>

                                                            <span>
                                                                {job.qualification}
                                                            </span>

                                                        </div>

                                                    )}

                                                </div>


                                                {/* PDF BUTTONS */}

                                                <div className="job-actions">

                                                    {job.download ? (

                                                        <div className="job-pdf-actions">

                                                            {/* VIEW PDF */}

                                                            <a
                                                                href={getPdfViewUrl(
                                                                    job.download
                                                                )}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="job-apply-btn"
                                                            >
                                                                📄 View PDF
                                                            </a>


                                                            {/* DOWNLOAD PDF */}

                                                            <a
                                                                href={getPdfDownloadUrl(
                                                                    job.job_id || job.id
                                                                )}
                                                                className="job-download-btn"
                                                            >
                                                                ⬇ Download PDF
                                                            </a>

                                                        </div>

                                                    ) : (

                                                        <button
                                                            type="button"
                                                            className="job-apply-btn"
                                                            disabled
                                                        >
                                                            PDF Not Available
                                                        </button>

                                                    )}

                                                </div>

                                            </article>

                                        );

                                    })}

                                </div>


                                {/* RESPONSIVE PAGINATION */}

                                {totalPages > 1 && (

                                    <nav
                                        className="jobs-pagination"
                                        aria-label="Jobs pagination"
                                    >

                                        {/* PREVIOUS */}

                                        <button
                                            type="button"
                                            className="pagination-btn"
                                            disabled={currentPage === 1}
                                            onClick={() =>
                                                changePage(currentPage - 1)
                                            }
                                            aria-label="Previous page"
                                        >
                                            ‹ Previous
                                        </button>


                                        {/* PAGE NUMBERS */}

                                        <div className="pagination-numbers">

                                            {getVisiblePages().map(
                                                (page, index) => (

                                                    page === "..." ? (

                                                        <span
                                                            key={`ellipsis-${index}`}
                                                            className="pagination-ellipsis"
                                                            aria-hidden="true"
                                                        >
                                                            ...
                                                        </span>

                                                    ) : (

                                                        <button
                                                            type="button"
                                                            key={page}
                                                            className={`pagination-number ${
                                                                currentPage === page
                                                                    ? "active"
                                                                    : ""
                                                            }`}
                                                            onClick={() =>
                                                                changePage(page)
                                                            }
                                                            aria-label={`Go to page ${page}`}
                                                            aria-current={
                                                                currentPage === page
                                                                    ? "page"
                                                                    : undefined
                                                            }
                                                        >
                                                            {page}
                                                        </button>

                                                    )

                                                )
                                            )}

                                        </div>


                                        {/* NEXT */}

                                        <button
                                            type="button"
                                            className="pagination-btn"
                                            disabled={
                                                currentPage === totalPages
                                            }
                                            onClick={() =>
                                                changePage(currentPage + 1)
                                            }
                                            aria-label="Next page"
                                        >
                                            Next ›
                                        </button>

                                    </nav>

                                )}


                                {/* PAGINATION INFO */}

                                {totalPages > 1 && (

                                    <div className="pagination-info">

                                        Showing{" "}

                                        <strong>
                                            {startIndex + 1}
                                        </strong>

                                        {" "}to{" "}

                                        <strong>
                                            {Math.min(
                                                endIndex,
                                                jobs.length
                                            )}
                                        </strong>

                                        {" "}of{" "}

                                        <strong>
                                            {jobs.length}
                                        </strong>

                                        {" "}jobs

                                    </div>

                                )}

                            </>

                        )}

                    </div>

                </section>

            </main>

            <Footer />
        </>
    );

}

export default Jobs;
